import type { ReactNode } from 'react';
import { useI18n } from '../i18n/index.tsx';
import type { Server, TopologyPresetEntry, TopologySlot } from '../api/types.ts';
import { Button, Field, Notice } from '../components/ui.tsx';
import { assignServer, assignedServerIds, usableServers, type WizardState } from './state.ts';

/**
 * Putting each server in its place.
 *
 * Only servers we have checked and can obtain root on are offered: a server
 * that would fail at planning is better left out of the list than presented
 * and then refused three steps later.
 */
export function ServersStep({
  presets,
  servers,
  state,
  onChange,
  onAddServer,
}: {
  presets: readonly TopologyPresetEntry[];
  servers: readonly Server[];
  state: WizardState;
  onChange: (state: WizardState) => void;
  onAddServer: () => void;
}): ReactNode {
  const { t } = useI18n();
  const preset = presets.find((entry) => entry.preset === state.preset);
  const usable = usableServers(servers);

  if (!preset) return null;

  if (usable.length === 0) {
    return (
      <Notice
        tone="warning"
        actions={
          <Button size="sm" onClick={onAddServer}>
            {t('servers.add')}
          </Button>
        }
      >
        {t('wizard.servers.noneReady')}
      </Notice>
    );
  }

  return (
    <div className="slots">
      <p className="muted">{t('wizard.servers.help')}</p>

      {preset.slots.map((slot) => (
        <SlotEditor key={slot.id} slot={slot} servers={usable} state={state} onChange={onChange} />
      ))}

      <div>
        <Button variant="secondary" size="sm" onClick={onAddServer}>
          {t('wizard.servers.addMore')}
        </Button>
      </div>
    </div>
  );
}

function SlotEditor({
  slot,
  servers,
  state,
  onChange,
}: {
  slot: TopologySlot;
  servers: readonly Server[];
  state: WizardState;
  onChange: (state: WizardState) => void;
}): ReactNode {
  const { t, pick } = useI18n();
  const chosen = state.assignments[slot.id] ?? [];
  // Always one empty row beyond what is filled, up to the slot's maximum, so
  // adding a second entry server needs no "add" button.
  const rows = Math.min(Math.max(slot.min, chosen.filter(Boolean).length + 1), slot.max);
  const usedElsewhere = new Set(assignedServerIds(state).filter(Boolean));

  return (
    <section className="slot">
      <h3>{pick(slot.label)}</h3>
      <p className="muted">{pick(slot.help)}</p>

      <div className="slot__rows">
        {Array.from({ length: rows }, (_, index) => {
          const value = chosen[index] ?? '';
          return (
            <Field
              key={index}
              label={<span className="visually-hidden">{pick(slot.label)}</span>}
              required
            >
              {(props) => (
                <select
                  {...props}
                  className="input"
                  value={value}
                  onChange={(event) =>
                    onChange(assignServer(state, slot.id, index, event.target.value || undefined))
                  }
                >
                  <option value="">{t('wizard.servers.choose')}</option>
                  {servers.map((server) => {
                    const taken = usedElsewhere.has(server.id) && server.id !== value;
                    return (
                      <option key={server.id} value={server.id} disabled={taken}>
                        {server.name}
                        {server.locationLabel ? ` — ${server.locationLabel}` : ''}
                        {taken ? ` (${t('wizard.servers.alreadyUsed')})` : ''}
                      </option>
                    );
                  })}
                </select>
              )}
            </Field>
          );
        })}
      </div>
    </section>
  );
}
